import { createContext, useContext, useState, useEffect, useRef, type ReactNode } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { opLogService, type ContagemOpLog } from '../db/op-log.service';
import { syncService } from '../services/sync.service';

interface OfflineContextType {
  isOnline: boolean;
  isSyncing: boolean;
  /** Operações que ainda não chegaram ao servidor (pending + syncing) */
  pendingOperations: number;
  /** Operações que esgotaram as 3 tentativas — exigem ação do vendedor */
  failedOperations: number;
  /** Motivo da falha mais recente, para a UI não mostrar só um botão cego */
  ultimoErro: string | null;
  lastSyncAt: Date | null;
  triggerSync: () => Promise<void>;
  /** Devolve as operações em falha para a fila e tenta drenar de novo */
  reenviarFalhas: () => Promise<void>;
}

const OfflineContext = createContext<OfflineContextType | undefined>(undefined);

// AIDEV-NOTE: 60s — curto o bastante para o pedido subir logo que a rede volta num tunel/elevador
// (o evento 'online' nao dispara quando o sinal apenas melhora), longo o bastante para nao pesar
// na bateria. triggerSync() sai cedo quando offline ou ja sincronizando, entao o tick e barato.
const SYNC_HEARTBEAT_MS = 60_000;

// AIDEV-NOTE: valor inicial do useLiveQuery — evita um primeiro render com `undefined`, que faria
// o indicador piscar "sem pendencias" antes da primeira leitura do IndexedDB.
const CONTAGEM_VAZIA: ContagemOpLog = { pending: 0, syncing: 0, failed: 0, ultimoErro: null };

/**
 * Provider de Offline Sync
 * AIDEV-NOTE: Gerencia estado de conectividade e sincronização
 */
export function OfflineProvider({ children }: { children: ReactNode }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState<Date | null>(null);
  const isSyncingRef = useRef(false);

  // AIDEV-NOTE: contagem REATIVA via useLiveQuery — o Dexie reexecuta a query sozinho quando o
  // op_log muda. Sem isso o contador so atualizava no mount e ao fim de cada sync: o vendedor
  // finalizava um pedido sem sinal e o indicador na AppBar continuava zerado, que e exatamente a
  // duvida que o fez achar que tinha perdido o pedido.
  // AIDEV-NOTE: o catch NAO e decorativo — o OfflineProvider envolve todo o app (inclusive a tela
  // de login) e o useLiveQuery relanca a rejeicao no render. Sem ele, um IndexedDB indisponivel
  // (aba anonima, cota, banco bloqueado por outra aba) derrubaria a tela inteira por um contador.
  const contagem = useLiveQuery(
    () => opLogService.contarPorStatus().catch(() => CONTAGEM_VAZIA),
    [],
    CONTAGEM_VAZIA
  );

  useEffect(() => {
    // Atualiza status online/offline
    const handleOnline = () => {
      setIsOnline(true);
      // Trigger sync automático quando volta online
      triggerSync();
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    // AIDEV-NOTE: Regressao de campo (2026-10) — antes o drain SO rodava no evento 'online'. O
    // fluxo real do vendedor e fechar o app sem sinal na rua e reabrir ja conectado: nesse caminho
    // 'online' nunca dispara e o pedido finalizado offline ficava pendente para sempre. Nao ha
    // Background Sync registrado no SW (sw.ts), entao estes tres gatilhos sao a unica garantia:
    //   1. mount (app aberto ja online)  2. aba volta ao primeiro plano  3. heartbeat de 60s.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') triggerSync();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    triggerSync();

    const heartbeat = setInterval(() => triggerSync(), SYNC_HEARTBEAT_MS);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(heartbeat);
    };
  }, []);

  const triggerSync = async () => {
    // AIDEV-NOTE: navigator.onLine + ref evitam closure stale do handleOnline registrado com deps []
    if (isSyncingRef.current || !navigator.onLine) return;

    isSyncingRef.current = true;
    setIsSyncing(true);

    try {
      await syncService.syncPendingOperations();
      setLastSyncAt(new Date());
    } catch (error) {
      console.error('Erro ao sincronizar:', error);
    } finally {
      isSyncingRef.current = false;
      setIsSyncing(false);
    }
  };

  // AIDEV-NOTE: reenfileira ANTES de drenar — sem isso o drain correria sobre as ops ainda
  // marcadas como 'failed' (getPendingOperations so enxerga 'pending') e o botao nao faria nada.
  const reenviarFalhas = async () => {
    await opLogService.reenfileirarFalhas();
    await triggerSync();
  };

  const value: OfflineContextType = {
    isOnline,
    isSyncing,
    // pendentes = o que ainda nao chegou ao servidor, incluindo o que esta em voo
    pendingOperations: (contagem?.pending ?? 0) + (contagem?.syncing ?? 0),
    failedOperations: contagem?.failed ?? 0,
    ultimoErro: contagem?.ultimoErro ?? null,
    lastSyncAt,
    triggerSync,
    reenviarFalhas
  };

  return <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>;
}

/**
 * Hook de offline sync
 */
export function useOfflineSync() {
  const context = useContext(OfflineContext);

  if (context === undefined) {
    throw new Error('useOfflineSync deve ser usado dentro de OfflineProvider');
  }

  return context;
}
